import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry error tracking
 * @param {Object} options - Configuration options
 * @param {string} options.dsn - Sentry DSN
 * @param {string} options.environment - Environment name (development, staging, production)
 * @param {string} options.release - Release version
 */
export const initSentry = ({ dsn, environment = 'development', release } = {}) => {
  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not provided. Error tracking is disabled.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,
    enabled: process.env.NODE_ENV !== 'development',

    // Performance Monitoring
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),
      // Replay integration for session recording
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring sample rate
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Session Replay sample rate
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    // Filter out certain errors
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Filter out network errors that are expected
      if (error && error.message) {
        // Ignore 401 errors (handled by auth system)
        if (error.message.includes('401')) {
          return null;
        }

        // Ignore cancelled requests
        if (error.message.includes('cancelled') || error.message.includes('aborted')) {
          return null;
        }
      }

      return event;
    },

    // Add custom tags
    initialScope: {
      tags: {
        app: 'bigfoot-v2-frontend',
      },
    },
  });
};

/**
 * Set user context for Sentry
 * @param {Object} user - User object
 */
export const setSentryUser = (user) => {
  if (!user) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username || `${user.first_name} ${user.last_name}`.trim(),
    role: user.role?.name,
  });
};

/**
 * Clear user context from Sentry
 */
export const clearSentryUser = () => {
  Sentry.setUser(null);
};

/**
 * Manually capture an exception
 * @param {Error} error - Error object
 * @param {Object} context - Additional context
 */
export const captureException = (error, context = {}) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

/**
 * Manually capture a message
 * @param {string} message - Message to capture
 * @param {string} level - Severity level (info, warning, error)
 * @param {Object} context - Additional context
 */
export const captureMessage = (message, level = 'info', context = {}) => {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
};

/**
 * Add breadcrumb for debugging
 * @param {Object} breadcrumb - Breadcrumb data
 */
export const addBreadcrumb = (breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

/**
 * Set custom context
 * @param {string} key - Context key
 * @param {Object} value - Context value
 */
export const setContext = (key, value) => {
  Sentry.setContext(key, value);
};

export default Sentry;
